import { Button, Space, Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Routes } from '../../../../app/routes';
import IconWrapper from '../../../../shared/components/icon-wrapper/icon-wrapper';
import Table from '../../../../shared/components/table/table';
import { ClockHistoryIcon } from '../../../../shared/icons/clock-history-icon';
import { PasswordResetIcon } from '../../../../shared/icons/password-reset-icon';
import { PencilSquareIcon } from '../../../../shared/icons/pencil-square-icon';
import { SignatureResetIcon } from '../../../../shared/icons/signature-reset-icon';
import { TrashIcon } from '../../../../shared/icons/trash-icon';
import styles from '../../../../styles/components/scan-sign-requests-table.module.css';
import { openModal } from '../../../ui/store/ui-slice';
import { useGetUserQuery } from '../../../user/api/user-api';
import {
  clientUserDeleteModalId,
  clientUserFormModalId,
  clientUserPasswordResetModalId,
  clientUserSignatureResetModalId,
} from '../../constants/constants';
import { ClientUserTableModel } from '../../models/client-user-table-model';
import { setActiveClientUserId } from '../../store/client-users-slice';

interface Props {
  clientUsersDataSource: ClientUserTableModel[];
  isClientDataFetching: boolean;
}

const ClientUsersTable = ({ isClientDataFetching, clientUsersDataSource }: Props) => {
  const { t } = useTranslation(['common', 'client-users']);
  const dispatch = useDispatch();

  const { data: currentUserData } = useGetUserQuery();

  const actions = (formModalId: string, key: string) => {
    dispatch(openModal(formModalId));
    dispatch(setActiveClientUserId(key));
  };

  const columns: ColumnsType<ClientUserTableModel> = [
    {
      title: t('label.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('label.username'),
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: t('label.email'),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t('label.role'),
      dataIndex: 'roles',
      key: 'roles',
      render: (_, { roles }) => roles.map(role => (role ? t(`roles.${role}`, { ns: 'client-users' }) : '')).join(', '),
    },
    {
      title: t('label.unit'),
      dataIndex: 'unit',
      key: 'unit',
    },
    {
      title: t('label.groups'),
      dataIndex: 'groups',
      key: 'groups',
      width: 460,
    },
    {
      title: t('label.signature', { ns: 'client-users' }),
      dataIndex: 'signature',
      key: 'signature',
      className: 'text-nowrap',
      width: 200,
      render: (_, { hasSignature }) => (
        <Tag color={hasSignature ? 'success' : 'warning'}>{t(`label.options.${hasSignature}`)}</Tag>
      ),
    },
    {
      title: t('label.active', { ns: 'client-users' }),
      dataIndex: 'active',
      key: 'active',
      className: 'text-nowrap',
      width: 160,
      render: (_, { active }) => <Tag color={active ? 'success' : 'warning'}>{t(`label.options.${active}`)}</Tag>,
    },
    {
      title: t('clientUsersTable.label.2fa.2faEnabled', { ns: 'client-users' }),
      dataIndex: 'totpEnabled',
      key: 'totpEnabled',
      className: 'text-nowrap',
      width: 200,
      render: (_, { totpEnabled }) => (
        <Tag color={totpEnabled ? 'success' : 'warning'}>{t(`label.options.${totpEnabled}`)}</Tag>
      ),
    },
    {
      title: t('label.actions'),
      key: 'actions',
      fixed: 'right',
      width: 282,
      render: record => (
        <Space size="middle">
          <Tooltip mouseEnterDelay={0.5} title={t('action.edit')}>
            <Button
              onClick={() => actions(clientUserFormModalId, record.id)}
              type="text"
              shape="circle"
              icon={
                <IconWrapper>
                  <PencilSquareIcon />
                </IconWrapper>
              }
            />
          </Tooltip>
          <Tooltip mouseEnterDelay={0.5} title={t('action.history')}>
            <Link to={Routes.ClientUserTraceLogs.replace(':id', record.id)}>
              <Button
                type="text"
                shape="circle"
                icon={
                  <IconWrapper>
                    <ClockHistoryIcon />
                  </IconWrapper>
                }
              />
            </Link>
          </Tooltip>
          <Tooltip mouseEnterDelay={0.5} title={t('action.passwordReset', { ns: 'client-users' })}>
            <Button
              onClick={() => actions(clientUserPasswordResetModalId, record.id)}
              type="text"
              shape="circle"
              disabled={!record.canChangePassword}
              icon={
                <IconWrapper>
                  <PasswordResetIcon />
                </IconWrapper>
              }
            />
          </Tooltip>
          <Tooltip mouseEnterDelay={0.5} title={t('action.signatureReset', { ns: 'client-users' })}>
            <Button
              onClick={() => actions(clientUserSignatureResetModalId, record.id)}
              type="text"
              shape="circle"
              icon={
                <IconWrapper>
                  <SignatureResetIcon />
                </IconWrapper>
              }
            />
          </Tooltip>
          <Tooltip mouseEnterDelay={0.5} title={t('action.delete')}>
            <Button
              disabled={record.id === currentUserData?.id}
              onClick={() => actions(clientUserDeleteModalId, record.id)}
              type="text"
              shape="circle"
              icon={
                <IconWrapper
                  textProps={{
                    type: 'warning',
                  }}
                >
                  <TrashIcon />
                </IconWrapper>
              }
            />
          </Tooltip>
        </Space>
      ),
    },
  ];
  return (
    <div className={styles.tableContainer}>
      <Table
        tableProps={{
          rowKey: ({ id }: ClientUserTableModel) => id,
          scroll: {
            x: 2000,
            y: '100%',
          },
          loading: isClientDataFetching,
          dataSource: clientUsersDataSource,
          columns,
        }}
      />
    </div>
  );
};

export default ClientUsersTable;
